import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, UserCheck, FileText, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export const Privacy = () => {
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Privacy Policy - Crescent Electrical Ltd"
        description="Privacy Policy for Crescent Electrical Ltd. How we collect, use and protect your personal information."
        canonical="https://crescentelectrical.co.uk/privacy"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2563EB] to-blue-700 text-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-10 w-10" />
            <h1 className="text-4xl sm:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            How Crescent Electrical Ltd collects, uses and protects your personal information
          </p>
          <p className="text-sm text-blue-200 mt-4">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="prose prose-blue max-w-none">
              
              {/* Introduction */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Who We Are</h2>
                <p className="text-gray-700 mb-3">
                  Crescent Electrical Ltd ("we", "us", "our") is a UK limited company providing electrical contracting services across Greater London.
                </p>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="space-y-2 text-sm">
                      <p><strong>Company name:</strong> Crescent Electrical Ltd</p>
                      <p><strong>Company number:</strong> 10335734</p>
                      <p><strong>Registered office:</strong> 67a Station Road, London, England, E4 7BU</p>
                      <p><strong>Place of registration:</strong> Registered in England and Wales</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* What Information We Collect */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Eye className="h-6 w-6 text-[#2563EB]" />
                  2. What Information We Collect
                </h2>
                <p className="text-gray-700 mb-4">
                  When you use our website or request our services, we may collect the following types of personal information:
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Information You Provide Directly:</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li><strong>Contact details:</strong> Name, email address, telephone number, mobile number</li>
                  <li><strong>Property information:</strong> Postcode, property type, number of bedrooms, property status</li>
                  <li><strong>Enquiry details:</strong> Service requirements, project descriptions, preferred timeframes</li>
                  <li><strong>Uploaded documents:</strong> Property photographs, floor plans, existing electrical certificates (EICRs), building plans</li>
                  <li><strong>Quote information:</strong> Rewire type, property access details, additional requirements</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Information Collected Automatically:</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Website usage:</strong> Pages visited, time spent on pages, navigation paths</li>
                  <li><strong>Device information:</strong> Browser type, operating system, device type, IP address</li>
                  <li><strong>Analytics data:</strong> Google Analytics 4 collects anonymised usage statistics (when you consent)</li>
                  <li><strong>Advertising data:</strong> Google Ads conversion tracking, click identifiers (gclid), UTM campaign parameters (when you consent)</li>
                </ul>
              </div>

              {/* Why We Collect Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-6 w-6 text-[#2563EB]" />
                  3. Why We Collect Information & Legal Basis
                </h2>
                <p className="text-gray-700 mb-4">
                  We process your personal information under the following lawful bases under UK GDPR:
                </p>

                <div className="space-y-4">
                  <Card className="border-gray-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Contract Performance & Pre-Contractual Steps</h4>
                      <p className="text-sm text-gray-700 mb-2">We use your information to:</p>
                      <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                        <li>Respond to your enquiries and quote requests</li>
                        <li>Prepare quotations and estimates</li>
                        <li>Schedule site surveys and property visits</li>
                        <li>Carry out electrical installation, testing and certification work</li>
                        <li>Issue invoices and process payments</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-gray-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Legal Obligation</h4>
                      <p className="text-sm text-gray-700 mb-2">We are required by law to:</p>
                      <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                        <li>Retain electrical installation certificates and test records</li>
                        <li>Maintain records for Building Regulations compliance</li>
                        <li>Keep financial records for HMRC</li>
                        <li>Comply with health and safety requirements</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-gray-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Legitimate Interests</h4>
                      <p className="text-sm text-gray-700 mb-2">We have a legitimate business interest in:</p>
                      <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                        <li>Operating and securing our website</li>
                        <li>Preventing fraud and misuse</li>
                        <li>Keeping records of customer communications</li>
                        <li>Improving our services</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-gray-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Consent</h4>
                      <p className="text-sm text-gray-700 mb-2">We ask for your consent to:</p>
                      <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                        <li>Use Google Analytics to understand website usage</li>
                        <li>Use Google Ads conversion tracking to measure advertising effectiveness</li>
                        <li>Send you marketing communications (we do NOT automatically subscribe you to marketing when you request a quote)</li>
                      </ul>
                      <p className="text-sm text-gray-700 mt-2">You can withdraw consent at any time via our <Link to="/cookie-settings" className="text-[#2563EB] hover:underline">Cookie Settings</Link>.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Data Retention */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How Long We Keep Your Information</h2>
                <p className="text-gray-700 mb-3">
                  We retain your personal information only as long as necessary:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Quote enquiries (no work carried out):</strong> 2 years</li>
                  <li><strong>Electrical installation records & certificates:</strong> Minimum 7 years (legal requirement)</li>
                  <li><strong>Financial records:</strong> 6 years (HMRC requirement)</li>
                  <li><strong>Website analytics:</strong> 26 months (Google Analytics default)</li>
                  <li><strong>Marketing consent:</strong> Until you withdraw consent or 3 years of inactivity</li>
                </ul>
              </div>

              {/* Data Sharing */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Who We Share Your Information With</h2>
                <p className="text-gray-700 mb-3">
                  We do not sell your personal information. We may share it with the following trusted service providers:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Email service provider (Resend):</strong> To send quote confirmations and notifications</li>
                  <li><strong>Google Analytics & Google Ads:</strong> For website analytics and advertising measurement (when you consent)</li>
                  <li><strong>Cloud hosting provider (Emergent):</strong> Our website and database infrastructure</li>
                  <li><strong>NICEIC/scheme providers:</strong> For certification and compliance purposes where applicable</li>
                  <li><strong>Payment processors:</strong> If you make payments (e.g., Stripe, bank transfers)</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  Some of these services may process data outside the UK. Where this occurs, we ensure appropriate safeguards are in place (e.g., Standard Contractual Clauses, adequacy decisions).
                </p>
              </div>

              {/* Data Security */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Lock className="h-6 w-6 text-[#2563EB]" />
                  6. How We Protect Your Information
                </h2>
                <p className="text-gray-700 mb-3">
                  We take appropriate technical and organisational measures to protect your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>HTTPS encryption for all website communications</li>
                  <li>Secure server-side storage of sensitive data</li>
                  <li>File upload restrictions and validation</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security monitoring</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <UserCheck className="h-6 w-6 text-[#2563EB]" />
                  7. Your Rights Under UK GDPR
                </h2>
                <p className="text-gray-700 mb-3">
                  You have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Right of access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Right to rectification:</strong> Ask us to correct inaccurate information</li>
                  <li><strong>Right to erasure:</strong> Request deletion of your information (subject to legal retention requirements)</li>
                  <li><strong>Right to restrict processing:</strong> Ask us to limit how we use your information</li>
                  <li><strong>Right to data portability:</strong> Receive your information in a portable format</li>
                  <li><strong>Right to object:</strong> Object to processing based on legitimate interests or for marketing purposes</li>
                  <li><strong>Right to withdraw consent:</strong> Withdraw consent for analytics and advertising tracking</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  To exercise any of these rights, please contact us at <a href="mailto:info@electriccertificate.co.uk" className="text-[#2563EB] hover:underline">info@electriccertificate.co.uk</a>
                </p>
              </div>

              {/* Cookies */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies & Tracking Technologies</h2>
                <p className="text-gray-700 mb-3">
                  We use cookies and similar technologies on our website. For detailed information, please see our separate <Link to="/cookies" className="text-[#2563EB] hover:underline">Cookie Policy</Link>.
                </p>
                <p className="text-gray-700">
                  You can manage your cookie preferences at any time via <Link to="/cookie-settings" className="text-[#2563EB] hover:underline">Cookie Settings</Link>.
                </p>
              </div>

              {/* Complaints */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Right to Complain</h2>
                <p className="text-gray-700 mb-3">
                  If you are unhappy with how we have handled your personal information, you have the right to complain to the UK's supervisory authority:
                </p>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-6">
                    <p className="font-semibold text-gray-900 mb-2">Information Commissioner's Office (ICO)</p>
                    <p className="text-sm text-gray-700">Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">ico.org.uk</a></p>
                    <p className="text-sm text-gray-700">Helpline: 0303 123 1113</p>
                  </CardContent>
                </Card>
              </div>

              {/* Contact */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="h-6 w-6 text-[#2563EB]" />
                  10. Contact Us About Privacy
                </h2>
                <p className="text-gray-700 mb-3">
                  If you have any questions about this Privacy Policy or how we handle your personal information, please contact us:
                </p>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="space-y-2 text-sm">
                      <p><strong>Email:</strong> <a href="mailto:info@electriccertificate.co.uk" className="text-[#2563EB] hover:underline">info@electriccertificate.co.uk</a></p>
                      <p><strong>Phone:</strong> <a href="tel:07711233685" className="text-[#2563EB] hover:underline">07711 233685</a></p>
                      <p><strong>Post:</strong> Crescent Electrical Ltd, 67a Station Road, London, E4 7BU</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Changes */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
                <p className="text-gray-700">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "last updated" date. For significant changes, we may provide additional notice.
                </p>
              </div>

            </div>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/cookies">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Cookie Policy</h3>
                  <p className="text-sm text-gray-600">Learn about cookies and tracking technologies we use</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/terms">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Website Terms</h3>
                  <p className="text-sm text-gray-600">Read our terms and conditions of use</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
